import pawsWithdrawService from "@tvg/api/paws/withdraw";
import { makeBraintreeCardWithdrawal } from "@tvg/api/paws/braintreeCC";
import { updateBalance } from "@tvg/shared-actions/UserActions";
import * as mediatorClassic from "@tvg/mediator-classic/src";
import { getWithdrawalLimits } from "@tvg/sh-lib-paws/hooks/withdrawalLimits";
import { isTvg5 } from "@tvg/utils/generalUtils";
import { get } from "lodash";
import { PaymentTypesAvailable } from "@tvg/api/paws/types";
import { isDesktop } from "../../../../utils";
import {
  gtmAuthorizeWithdrawError,
  gtmAuthorizeWithdrawSuccess,
  gtmWithdrawAuthorize,
  gtmWithdrawValidationError
} from "../../../../gtm";
import { OnSubmitWithdrawArgs } from "../types";
import { insufficientFundsErrorMessage } from "./errorHandling";

type NormalizedWithdrawalSuccess = {
  balance?: string;
  transactionId?: string;
};

export function normalizeWithdrawalSuccessData(
  data: Record<string, unknown>
): NormalizedWithdrawalSuccess {
  const balance = typeof data.balance === "string" ? data.balance : undefined;
  const rawTx = data.transactionUUID || data.uuid;
  const transactionId = rawTx ? String(rawTx) : undefined;
  return { balance, transactionId };
}

/** Matches UWT Mazooma (`MZM`): keep loading until follow-up flow when a transaction id exists. */
function shouldClearWithdrawLoading(
  transactionId: string | undefined,
  paymentType: OnSubmitWithdrawArgs["paymentType"]
): boolean {
  return !transactionId || paymentType !== "MZM";
}

async function finalizeWithdrawalSuccess(
  args: OnSubmitWithdrawArgs,
  normalized: NormalizedWithdrawalSuccess
): Promise<void> {
  const {
    paymentType,
    accountId,
    value,
    dispatch,
    setTransactionID,
    setWithdrawalLimit,
    capiMaxWithdrawAmount,
    setIsLoading,
    setSuccessMessagePmType,
    setShowSuccessMessage,
    refetchAvailableMethods
  } = args;

  gtmAuthorizeWithdrawSuccess(paymentType, accountId, value);
  if ((!isDesktop || isTvg5()) && normalized.balance) {
    dispatch(updateBalance(normalized.balance));
  } else if (normalized.balance) {
    mediatorClassic.dispatch("BALANCE_UPDATE", normalized.balance);
  }
  if (normalized.transactionId) {
    setTransactionID(normalized.transactionId);
  }

  await getWithdrawalLimits(
    accountId,
    setWithdrawalLimit,
    capiMaxWithdrawAmount
  );

  if (shouldClearWithdrawLoading(normalized.transactionId, paymentType)) {
    setIsLoading(false);
  }
  if (paymentType) {
    setSuccessMessagePmType(paymentType);
  }
  setShowSuccessMessage(true);

  refetchAvailableMethods();
}

function handleWithdrawalFailure(
  args: OnSubmitWithdrawArgs,
  err: unknown
): void {
  const {
    paymentType,
    accountId,
    pawsErrorMessages,
    setErrorMessage,
    setShowSuccessMessage,
    setIsLoading
  } = args;

  const errorCode = String(get(err, "response.data.code", ""));
  const errorByCode =
    get(pawsErrorMessages, `${errorCode}`) || get(pawsErrorMessages, "default");
  gtmAuthorizeWithdrawError(
    paymentType,
    accountId,
    get(errorByCode, "description", "")
  );

  setErrorMessage((prevState) => ({
    ...prevState,
    isOpen: true,
    errorCode,
    isRetry: false
  }));
  setShowSuccessMessage(false);

  setIsLoading(false);
}

async function runWithdrawalSubmission(
  args: OnSubmitWithdrawArgs,
  executeWithdraw: () => Promise<{ data?: unknown } | null | undefined>
): Promise<void> {
  const {
    value,
    minWithdrawAmount,
    maxWithdrawAmount,
    paymentType,
    accountId,
    setShowAmountWarning,
    setEnableDepositPage,
    setShowSuccessMessage
  } = args;

  setEnableDepositPage(false);
  setShowSuccessMessage(false);

  if (+value < +minWithdrawAmount || +value > maxWithdrawAmount) {
    setShowAmountWarning(true);
    gtmWithdrawValidationError({
      paymentType,
      accountID: accountId,
      errorMessage: insufficientFundsErrorMessage({
        minWithdrawAmount,
        value,
        userBalance: maxWithdrawAmount
      })
    });
    return;
  }

  try {
    gtmWithdrawAuthorize({
      paymentType,
      accountID: accountId
    });
    args.setIsLoading(true);

    const response = await executeWithdraw();
    const rawData = response?.data;

    if (rawData && typeof rawData === "object") {
      const normalized = normalizeWithdrawalSuccessData(
        rawData as Record<string, unknown>
      );
      await finalizeWithdrawalSuccess(args, normalized);
    } else {
      args.setIsLoading(false);
    }
  } catch (err) {
    handleWithdrawalFailure(args, err);
  }
}

export const onSubmitWithdraw = async (args: OnSubmitWithdrawArgs) => {
  const {
    paymentType,
    selectedPaymentMethodId,
    accountId,
    value,
    requestBody
  } = args;

  await runWithdrawalSubmission(args, () =>
    pawsWithdrawService.postWithdraw(paymentType as PaymentTypesAvailable, {
      amount: +value,
      accountId,
      paymentMethodId: +selectedPaymentMethodId,
      achId: +selectedPaymentMethodId,
      ...requestBody
    })
  );
};

export const onBraintreeSubmitWithdraw = async (args: OnSubmitWithdrawArgs) => {
  const { accountId, value, selectedPaymentMethodId } = args;

  await runWithdrawalSubmission(args, () =>
    makeBraintreeCardWithdrawal(accountId, {
      amount: +value,
      cardToken: selectedPaymentMethodId
    })
  );
};
